import React, { useEffect } from "react";
import axios from "axios";

import { useState } from "react";

import FormControl from "@mui/material/FormControl";

import DefaultConfiguredFormState from "./DefaultConfiguredFormState";
import { KeyboardEvent } from "react";

import FormInputRow from "./FormInputRow";
import FormRowState from "./FormRowState";
import FormRowModeStateType from "./FormRowModeStateType";
import FormConfig from "./FormConfig";
import { Skeleton } from "@mui/material";
import { Typography } from "@mui/material";

import GetCSRFToken from "../../../AuthComponents/GetCSRFToken";
import Link from "@mui/material/Link";
import Box from "@mui/material/Box";

type ConfiguredFormProps = {
  config: FormConfig | undefined;
  setConfig?: (
    newConfig: (FormConfig | undefined) | ((oldConfig: FormConfig | undefined) => FormConfig | undefined)
  ) => void;
  refreshing: boolean;
  setRefreshing: (newRefreshing: boolean) => void;
  disabled?: boolean;
  disableFirstRow?: boolean;
  state: FormRowState[];
  setState: (newState: FormRowState[] | ((oldState: FormRowState[]) => FormRowState[])) => void;
  canSubmit?: boolean;
  handleSubmit?: () => void;
  catchEnterKeyDownToSubmit?: (e: KeyboardEvent) => void; // overrides the props cansubmit and handle submit to make enter work for nested forms
};

function ConfiguredForm(props: ConfiguredFormProps) {
  function refreshForm(endpoint: string | undefined) {
    props.setRefreshing(true);
    if (endpoint !== undefined) {
      axios
        .post(endpoint, { config: props.config, state: props.state }, { headers: { "X-CSRFToken": GetCSRFToken() } })
        .then((response) => {
          if (props.setConfig !== undefined) {
            props.setConfig({ ...response.data, rowStates: undefined });
            if ("rowStates" in response.data) {
              props.setState(response.data["rowStates"]);
            } else {
              props.setState(DefaultConfiguredFormState(response.data));
            }
          }
          props.setRefreshing(false);
        })
        .catch((error) => {
          if (process.env.NODE_ENV === "development") {
            console.log(error);
          }
          props.setRefreshing(false);
        });
    }
  }

  const [editEnabled, setEditEnabled] = useState(
    props.config?.enableEditText === undefined || props.config?.enableEditText === null
  );

  useEffect(() => {
    if (props.config !== undefined) {
      setEditEnabled(props.config.enableEditText === undefined || props.config?.enableEditText === null);
    }
  }, [props.config]);

  const disabled =
    props.disabled || (props.config !== undefined && props.config.disabled) || props.refreshing || !editEnabled;
  const rowStateList: FormRowState[] = props.state;
  const setRowStateList = props.setState;

  function catchEnterKeyDownToSubmit(e: KeyboardEvent) {
    if (e.key === "Enter" && props.canSubmit && props.handleSubmit !== undefined) {
      props.handleSubmit();
    }
  }

  return props.config === undefined || props.config.formConfig === undefined ? (
    <Skeleton variant="rectangular" />
  ) : (
    <>
      {props.config.enableEditText === undefined ||
      props.config.enableEditText === null ||
      (editEnabled && (props.config.disableEditText === undefined || props.config.disableEditText === null)) ? null : (
        <Box sx={{ display: "flex", justifyContent: "center" }}>
          <Link
            component="button"
            color="text.secondary"
            variant="body2"
            onClick={() => setEditEnabled(!editEnabled)}
            sx={{ justifySelf: "center" }}
          >
            {editEnabled ? props.config.disableEditText : props.config.enableEditText}
          </Link>
        </Box>
      )}
      {props.config.description ? (
        <Typography variant="body1" color="text.primary" sx={{ pb: 2 }}>
          {props.config.description}
        </Typography>
      ) : null}
      {props.config.formError ? (
        <Typography variant="body1" color="error" sx={{ pb: 2 }}>
          {props.config.formError}
        </Typography>
      ) : null}
      {props.config.formWarning ? (
        <Typography variant="body1" color="warning.text" sx={{ pb: 2, whiteSpace: "pre-line" }}>
          {props.config.formWarning}
        </Typography>
      ) : null}
      <FormControl sx={{ display: "flex", flexDirection: "column", gap: 2, flex: 1 }}>
        {props.config.formConfig.map((config, index) => (
          <FormInputRow
            key={index}
            disabled={disabled || (props.disableFirstRow && index === 0)}
            catchEnterKeyDownToSubmit={
              props.catchEnterKeyDownToSubmit === undefined
                ? catchEnterKeyDownToSubmit
                : props.catchEnterKeyDownToSubmit
            }
            config={config}
            setNestedConfigAtIndex={(
              newConfig: (FormConfig | undefined) | ((oldConfig: FormConfig | undefined) => FormConfig | undefined),
              nestedIndex: number
            ) => {
              if (props.setConfig !== undefined) {
                props.setConfig((oldConfig: FormConfig | undefined) => {
                  if (oldConfig === undefined) {
                    return oldConfig;
                  } else {
                    return {
                      ...oldConfig,
                      formConfig: [
                        ...oldConfig.formConfig.slice(0, index),
                        {
                          ...oldConfig.formConfig[index],
                          inputOptions: [
                            ...oldConfig.formConfig[index].inputOptions.slice(0, nestedIndex),
                            {
                              ...oldConfig.formConfig[index].inputOptions[nestedIndex],
                              nestedFormConfig:
                                typeof newConfig === "function"
                                  ? newConfig(oldConfig.formConfig[index].inputOptions[nestedIndex].nestedFormConfig)
                                  : newConfig,
                            },
                            ...oldConfig.formConfig[index].inputOptions.slice(nestedIndex + 1),
                          ],
                        },
                        ...oldConfig.formConfig.slice(index + 1),
                      ],
                    };
                  }
                });
              }
            }}
            refresh={(endpoint: string | undefined) => refreshForm(endpoint)}
            refreshing={props.refreshing || false}
            currentState={rowStateList[index]}
            // TODO: add bounds checking?
            updateFormRowStateIndex={(newRowModeIndex: number) =>
              setRowStateList((rsl: FormRowState[]): FormRowState[] => [
                ...rsl.slice(0, index),
                {
                  ...rsl[index],
                  currentModeIndex: newRowModeIndex,
                  originalRowState:
                    rsl[index]["originalRowState"] === undefined ? rsl[index] : rsl[index]["originalRowState"],
                },
                ...rsl.slice(index + 1),
              ])
            }
            updateFormRowStateAtIndex={(
              newState: FormRowModeStateType | ((oldState: FormRowState[]) => FormRowState[]),
              rowModeIndex?: number
            ) => {
              const indexToUpdate = rowModeIndex === undefined ? rowStateList[index].currentModeIndex : rowModeIndex;
              setRowStateList((rsl: FormRowState[]): FormRowState[] => [
                ...rsl.slice(0, index),
                {
                  ...rsl[index],
                  currentModeStates: [
                    ...rsl[index].currentModeStates.slice(0, indexToUpdate),
                    typeof newState === "function"
                      ? newState(rsl[index].currentModeStates[indexToUpdate] as FormRowState[])
                      : newState,
                    ...rsl[index].currentModeStates.slice(indexToUpdate + 1),
                  ],
                  originalRowState:
                    rsl[index]["originalRowState"] === undefined ? rsl[index] : rsl[index]["originalRowState"],
                },
                ...rsl.slice(index + 1),
              ]);
            }}
            updateFormRowErrorAtIndex={(newError: string | null, rowModeIndex?: number) => {
              const indexToUpdate = rowModeIndex === undefined ? rowStateList[index].currentModeIndex : rowModeIndex;
              const preError: (string | null)[] =
                rowStateList[index].currentModeErrors === undefined
                  ? new Array(indexToUpdate).fill(null)
                  : //@ts-expect-error
                    rowStateList[index].currentModeErrors.slice(0, indexToUpdate);
              const postError: (string | null)[] =
                rowStateList[index].currentModeErrors === undefined
                  ? new Array(rowStateList[index].currentModeStates.length - indexToUpdate - 1).fill(null)
                  : //@ts-expect-error
                    rowStateList[index].currentModeErrors.slice(indexToUpdate + 1);
              setRowStateList((rsl: FormRowState[]): FormRowState[] => [
                ...rsl.slice(0, index),
                {
                  ...rsl[index],
                  currentModeErrors: [...preError, newError, ...postError],
                },
                ...rsl.slice(index + 1),
              ]);
            }}
            updateFormRowStateAndErrorAtIndex={(
              newState: FormRowModeStateType | ((oldState: FormRowState[]) => FormRowState[]),
              newError: string | null,
              rowModeIndex?: number
            ) => {
              const indexToUpdate = rowModeIndex === undefined ? rowStateList[index].currentModeIndex : rowModeIndex;
              const preError: (string | null)[] =
                rowStateList[index].currentModeErrors === undefined
                  ? new Array(indexToUpdate).fill(null)
                  : //@ts-expect-error
                    rowStateList[index].currentModeErrors.slice(0, indexToUpdate);
              const postError: (string | null)[] =
                rowStateList[index].currentModeErrors === undefined
                  ? new Array(rowStateList[index].currentModeStates.length - indexToUpdate - 1).fill(null)
                  : //@ts-expect-error
                    rowStateList[index].currentModeErrors.slice(indexToUpdate + 1);
              setRowStateList((rsl: FormRowState[]): FormRowState[] => [
                ...rsl.slice(0, index),
                {
                  ...rsl[index],
                  currentModeErrors: [...preError, newError, ...postError],
                  currentModeStates: [
                    ...rsl[index].currentModeStates.slice(0, indexToUpdate),
                    typeof newState === "function"
                      ? newState(rsl[index].currentModeStates[indexToUpdate] as FormRowState[])
                      : newState,
                    ...rsl[index].currentModeStates.slice(indexToUpdate + 1),
                  ],
                  originalRowState:
                    rsl[index]["originalRowState"] === undefined ? rsl[index] : rsl[index]["originalRowState"],
                },
                ...rsl.slice(index + 1),
              ]);
            }}
          />
        ))}
      </FormControl>
    </>
  );
}

export default ConfiguredForm;
// onDelete={() =>
//   setSelectedStrategyList([
//     ...selectedStrategyList.slice(0, index),
//     ...selectedStrategyList.slice(index + 1),
//   ])
